import React, { useState, useEffect, useCallback } from 'react';
import './WorkoutTab.css';
import WorkoutExecutor, { fmtWk } from './WorkoutExecutor';
import InfoModal, { InfoButton } from './InfoModal';

// Lista embutida de exercícios comuns (PT) — vira sugestão de autocomplete
// junto com os que o usuário já cadastrou.
const BUILTIN_EXERCISES = [
  // Peito
  'Supino reto', 'Supino inclinado', 'Supino declinado', 'Supino máquina',
  'Crucifixo', 'Crucifixo inclinado', 'Crossover', 'Voador (Peck deck)', 'Flexão de braço',
  // Costas
  'Puxada frente', 'Puxada aberta', 'Puxada supinada', 'Remada curvada',
  'Remada baixa', 'Remada unilateral (serrote)', 'Remada cavalinho', 'Barra fixa',
  'Pulldown', 'Levantamento terra',
  // Ombro
  'Desenvolvimento', 'Desenvolvimento máquina', 'Elevação lateral', 'Elevação frontal',
  'Crucifixo inverso', 'Encolhimento', 'Arnold press', 'Remada alta',
  // Bíceps
  'Rosca direta', 'Rosca alternada', 'Rosca martelo', 'Rosca scott',
  'Rosca concentrada', 'Rosca no cabo', 'Rosca inversa',
  // Tríceps
  'Tríceps testa', 'Tríceps corda', 'Tríceps pulley', 'Tríceps francês',
  'Tríceps coice', 'Mergulho no banco', 'Paralelas',
  // Pernas
  'Agachamento livre', 'Agachamento smith', 'Leg press', 'Cadeira extensora',
  'Cadeira flexora', 'Mesa flexora', 'Stiff', 'Afundo', 'Avanço', 'Búlgaro',
  'Cadeira adutora', 'Cadeira abdutora', 'Hack', 'Panturrilha em pé',
  'Panturrilha sentado', 'Elevação pélvica', 'Agachamento sumô',
  // Glúteo
  'Glúteo no cabo', 'Coice na máquina', 'Abdução no cabo',
  // Abdômen
  'Abdominal supra', 'Abdominal infra', 'Prancha', 'Elevação de pernas',
  'Abdominal oblíquo', 'Abdominal na roda', 'Abdominal na máquina',
  // Cardio
  'Esteira', 'Bicicleta', 'Elíptico', 'Escada (Stairmaster)', 'Pular corda',
];

const emptyExercise = () => ({ name: '', carga: '', series: '', reps: '', rest: 60 });

const fmtDateTime = (raw) => {
  if (!raw) return '';
  const m = /(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(raw);
  if (!m) return '';
  const [, , mo, d, h, mi] = m;
  return `${d}/${mo} ${h}:${mi}`;
};

const fmtSec = (total) => {
  const s = Math.max(0, Math.floor(total || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
};

// "Sexta", "Sábado" etc. — meio-dia fixo no parse pra não virar o dia
// errado por causa do fuso horário.
const fmtWeekday = (isoDate) => {
  if (!isoDate) return '';
  const d = new Date(`${isoDate}T12:00:00`);
  const wk = d.toLocaleDateString('pt-BR', { weekday: 'long' });
  return wk.charAt(0).toUpperCase() + wk.slice(1);
};

const fmtDateShort = (isoDate) => (isoDate ? isoDate.split('-').reverse().slice(0, 2).join('/') : '');

function WorkoutTab({ svc, onSessionSaved }) {
  const [routines, setRoutines] = useState([]);
  const [custom, setCustom] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null); // null = novo treino
  const [editName, setEditName] = useState('');
  const [editList, setEditList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);
  const [started, setStarted] = useState(null); // { id, name, exercises }
  const [doneSummary, setDoneSummary] = useState(null);
  // Com treino já concluído hoje, a lista de treinos fica escondida; este
  // estado abre ela de novo pra iniciar OUTRO treino.
  const [pickOther, setPickOther] = useState(false);

  // Treino de hoje — pego do backend (sobrevive a reload/troca de aba),
  // mostrado no topo: já concluído hoje, ou escolha rápida pra iniciar.
  const [workoutToday, setWorkoutToday] = useState(null);
  const [workoutTodayOpen, setWorkoutTodayOpen] = useState(false);
  const [showCargaGymInfo, setShowCargaGymInfo] = useState(false);
  const [previewId, setPreviewId] = useState(null); // treino escolhido pra ver/iniciar hoje

  // Histórico de treinos já realizados (inclui os de hoje, academia e relógio).
  const [history, setHistory] = useState([]);

  // Guarda id/name de QUALQUER jeito que o treino foi iniciado (popup de
  // editar, escolha rápida de hoje, sugestão da IA) — o "Reiniciar treino"
  // do resumo pós-treino usa isso pra buscar de novo a versão atual do
  // treino certo, em vez de reaproveitar o formulário do popup (que pode
  // estar mostrando outro treino qualquer).
  const [lastStarted, setLastStarted] = useState(null); // { id, name }

  // Popup de cadastro/edição de treino — substitui o formulário fixo que
  // antes ficava sempre visível na página.
  const [editorOpen, setEditorOpen] = useState(false);

  // Sugestões de treino por IA — cada uma vira uma aba própria (separada dos
  // treinos cadastrados) até o usuário decidir salvar ou iniciar direto.
  const [aiRoutines, setAiRoutines] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiSelectedIdx, setAiSelectedIdx] = useState(null);
  const [aiSaving, setAiSaving] = useState(false);

  // Frase motivacional pra treino — 1x por dia, cacheada no backend.
  const [motivacionalTreino, setMotivacionalTreino] = useState('');
  useEffect(() => {
    svc.getMotivacional('treino').then((d) => setMotivacionalTreino(d?.frase || '')).catch(() => {});
  }, [svc]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await svc.getWorkoutRoutines();
      if (d.error) throw new Error(d.error);
      setRoutines(d.routines || []);
      setCustom(d.custom_exercises || []);
      if (!selectedId && (d.routines || []).length > 0) {
        setSelectedId(d.routines[0].id);
      }
    } catch (e) { setError(e.message); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svc]);

  useEffect(() => { load(); }, [load]);

  const refreshWorkoutToday = useCallback(async () => {
    try {
      const d = await svc.getWorkoutToday();
      setWorkoutToday(d || null);
    } catch (e) { /* silêncio */ }
  }, [svc]);

  useEffect(() => { refreshWorkoutToday(); }, [refreshWorkoutToday]);

  const refreshHistory = useCallback(async () => {
    try {
      const d = await svc.getWorkoutHistory();
      setHistory(d?.sessions || []);
    } catch (e) { /* silêncio */ }
  }, [svc]);

  useEffect(() => { refreshHistory(); }, [refreshHistory]);

  // Sugestões de IA já geradas antes ficam salvas — recarrega ao montar,
  // não some ao trocar de aba ou recarregar a página.
  useEffect(() => {
    (async () => {
      try {
        const d = await svc.getWorkoutSuggestions();
        const list = d?.routines || [];
        setAiRoutines(list);
        if (list.length > 0) setAiSelectedIdx(0);
      } catch (e) { /* silêncio — sem sugestão salva ainda */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svc]);

  // Sempre que troca o treino selecionado, preenche a lista de edição.
  useEffect(() => {
    const r = routines.find((x) => x.id === selectedId);
    setEditName(r ? r.name : '');
    setEditList((r ? r.exercises : []).map((e) => ({ ...emptyExercise(), ...e })));
  }, [routines, selectedId]);

  const suggestions = [...new Set([...BUILTIN_EXERCISES, ...custom])].sort((a, b) =>
    a.localeCompare(b, 'pt'));

  // ---------- Edição do treino ----------
  const updateEx = (i, field, value) =>
    setEditList((l) => l.map((e, idx) => (idx === i ? { ...e, [field]: value } : e)));
  const addEx = () => setEditList((l) => [...l, emptyExercise()]);
  const removeEx = (i) => setEditList((l) => l.filter((_, idx) => idx !== i));
  const moveEx = (i, dir) => setEditList((l) => {
    const j = i + dir;
    if (j < 0 || j >= l.length) return l;
    const copy = [...l];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const novoTreino = () => { setSelectedId(null); setEditName(''); setEditList([]); setEditorOpen(true); };
  const openRoutine = (id) => { setSelectedId(id); setEditorOpen(true); };

  const saveRoutine = async () => {
    const name = editName.trim();
    if (!name) { setError('Dê um nome pro treino (ex: Pernas).'); return; }
    setSaving(true); setError(null);
    try {
      const clean = editList.filter((e) => (e.name || '').trim());
      const d = await svc.saveWorkoutRoutine({ id: selectedId, name, exercises: clean });
      if (d.error) throw new Error(d.error);
      setSelectedId(d.id);
      setSavedMsg(true);
      setTimeout(() => setSavedMsg(false), 2000);
      setCustom((c) => [...new Set([...c, ...d.exercises.map((e) => e.name)])]);
      await load();
      setSelectedId(d.id);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  const deleteRoutine = async () => {
    if (!selectedId) return;
    setSaving(true); setError(null);
    try {
      const d = await svc.deleteWorkoutRoutine(selectedId);
      if (d.error) throw new Error(d.error);
      setSelectedId(null);
      setEditorOpen(false);
      await load();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  // ---------- Execução ----------
  const startPickedToday = (routine) => {
    setDoneSummary(null);
    setPickOther(false);
    setLastStarted({ id: routine.id, name: routine.name });
    setStarted({ id: routine.id, name: routine.name, exercises: routine.exercises });
  };

  const handleFinish = (summary) => {
    setStarted(null);
    setDoneSummary(summary);
    refreshWorkoutToday();
    refreshHistory();
    if (onSessionSaved) onSessionSaved();
  };

  // Cobre o caso de ter clicado em "Finalizar" sem querer — recomeça o
  // MESMO treino que acabou de rodar (via lastStarted), buscando a versão
  // atual dele em routines (pega mudanças feitas depois); se não achar
  // (apagado/sugestão da IA nunca salva), cai pro que ficou no resumo, com
  // descanso padrão de 60s.
  const restartWorkout = () => {
    if (!lastStarted) { setDoneSummary(null); return; }
    const rotina = routines.find((r) => r.id === lastStarted.id);
    const exercises = rotina
      ? rotina.exercises
      : (doneSummary?.exercises || []).map((e) => ({
          name: e.name, carga: e.carga || '', series: e.series || '', reps: e.reps || '', rest: 60,
        }));
    setDoneSummary(null);
    setStarted({ id: rotina?.id ?? null, name: lastStarted.name, exercises });
  };

  // Reinicia o treino já concluído hoje (card do topo). Usa a lista de
  // exercícios do treino cadastrado (tem os descansos certos); se não achar
  // (treino apagado/sugestão da IA nunca salva), cai pro que ficou gravado
  // na sessão de hoje, com descanso padrão de 60s.
  const restartTodayWorkout = () => {
    const sessao = workoutToday?.session;
    if (!sessao) return;
    const rotina = routines.find((r) => r.name === sessao.routine_name);
    const exercises = rotina
      ? rotina.exercises
      : (sessao.exercises || []).map((e) => ({
          name: e.name, carga: e.carga || '', series: e.series || '', reps: e.reps || '', rest: 60,
        }));
    setDoneSummary(null);
    setLastStarted({ id: rotina?.id ?? null, name: sessao.routine_name || 'Treino' });
    setStarted({ id: rotina?.id ?? null, name: sessao.routine_name || 'Treino', exercises });
  };

  // ---------- Sugestões da IA ----------
  const suggestAi = async () => {
    setAiLoading(true); setAiError(null); setAiSelectedIdx(null);
    try {
      const d = await svc.suggestWorkoutRoutines();
      if (d.error) throw new Error(d.error);
      setAiRoutines(d.routines || []);
      if ((d.routines || []).length > 0) setAiSelectedIdx(0);
    } catch (e) { setAiError(e.message); } finally { setAiLoading(false); }
  };

  const startAiRoutine = (r) => {
    setDoneSummary(null);
    setPickOther(false);
    setLastStarted({ id: null, name: r.name });
    setStarted({ id: null, name: r.name, exercises: r.exercises });
  };

  const saveAiRoutine = async (r) => {
    setAiSaving(true); setAiError(null);
    try {
      const d = await svc.saveWorkoutRoutine({ id: null, name: r.name, exercises: r.exercises });
      if (d.error) throw new Error(d.error);
      await load();
      setSelectedId(d.id);
      const restante = aiRoutines.filter((x) => x !== r);
      setAiRoutines(restante);
      setAiSelectedIdx(null);
      // Persiste a lista sem a que acabou de virar treino de verdade —
      // senão ela reaparece na próxima vez que a aba abrir.
      svc.saveWorkoutSuggestions(restante).catch(() => {});
    } catch (e) { setAiError(e.message); } finally { setAiSaving(false); }
  };

  // ---------- Render ----------
  if (loading) return <div className="wk-view"><p className="wk-msg">Carregando…</p></div>;

  if (started) {
    return (
      <div className="wk-view">
        <WorkoutExecutor
          svc={svc}
          routineId={started.id}
          routineName={started.name}
          exercises={started.exercises}
          onFinish={handleFinish}
          onCancel={() => setStarted(null)}
        />
      </div>
    );
  }

  // Lista de treinos pra escolher e iniciar. Fica dentro do card "Treino de
  // hoje" enquanto nada foi feito; com treino já concluído, abre num card
  // próprio logo abaixo do botão "Iniciar treino".
  const renderPicker = () => (
    <>
              <div className="workout-total">
                <span>{workoutToday?.session ? 'Escolha o próximo treino' : 'Escolha o treino de hoje'}</span>
              </div>
              {routines.length === 0 && (
                <p className="workout-empty">Nenhum treino cadastrado ainda.</p>
              )}
              <div className="workout-pick-list">
                {routines.map((r) => (
                  <div className="workout-pick-row" key={r.id}>
                    <button
                      className="workout-pick"
                      onClick={() => setPreviewId((cur) => (cur === r.id ? null : r.id))}
                    >
                      <span>{r.name}</span>
                      <span className="workout-pick-count">({r.exercises.length})</span>
                    </button>
                    <button
                      className="workout-pick-edit"
                      onClick={() => openRoutine(r.id)}
                      aria-label={`Editar ${r.name}`}
                    >
                      ＋
                    </button>
                  </div>
                ))}
                <button className="workout-pick workout-pick-add" onClick={novoTreino}>
                  ＋ Novo treino
                </button>
              </div>

              {previewId && (() => {
                const escolhido = routines.find((r) => r.id === previewId);
                if (!escolhido) return null;
                return (
                  <>
                    <div className="workout-exs">
                      {escolhido.exercises.map((ex, idx) => (
                        <div className="workout-ex" key={idx}>
                          <span className="workout-ex-name">{ex.name}</span>
                          <span className="workout-ex-meta">
                            {[ex.carga && `${ex.carga}`, ex.series && `${ex.series}x`, ex.reps && `${ex.reps}`]
                              .filter(Boolean).join(' · ')}
                          </span>
                        </div>
                      ))}
                    </div>
                    <button
                      className="wk-btn wk-btn-start workout-start-btn"
                      onClick={() => startPickedToday(escolhido)}
                    >
                      ▶ Iniciar {escolhido.name}
                    </button>
                  </>
                );
              })()}
    </>
  );

  return (
    <div className="wk-view">
      {error && <p className="wk-msg wk-err">{error}</p>}

      {motivacionalTreino && (
        <div className="motivacional-banner">💪 {motivacionalTreino}</div>
      )}

      {/* ===== SUGESTÕES DE TREINO POR IA ===== */}
      <div className="wk-ai-section">
        <div className="wk-ai-head">
          <span className="wk-ai-title">🤖 Sugestões da IA</span>
          <button className="wk-btn wk-btn-ghost wk-ai-btn" onClick={suggestAi} disabled={aiLoading}>
            {aiLoading ? 'Pensando…' : aiRoutines.length ? 'Sugerir de novo' : 'Sugerir treinos'}
          </button>
        </div>

        {aiError && <p className="wk-msg wk-err">{aiError}</p>}

        {aiRoutines.length > 0 && (
          <>
            <div className="wk-days">
              {aiRoutines.map((r, i) => (
                <button
                  key={i}
                  className={`wk-day wk-day-ai has ${i === aiSelectedIdx ? 'active' : ''}`}
                  onClick={() => setAiSelectedIdx(i)}
                >
                  {r.name}
                </button>
              ))}
            </div>

            {aiSelectedIdx != null && aiRoutines[aiSelectedIdx] && (() => {
              const r = aiRoutines[aiSelectedIdx];
              return (
                <div className="wk-ai-preview">
                  {r.objetivo && <p className="wk-ai-objetivo">{r.objetivo}</p>}
                  <div className="wk-list">
                    {r.exercises.map((ex, i) => (
                      <div className="wk-ai-ex" key={i}>
                        <span className="wk-ai-ex-name">{ex.name}</span>
                        <span className="wk-ai-ex-meta">
                          {ex.series}x{ex.reps} · {ex.rest}s descanso
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="wk-actions">
                    <button className="wk-btn wk-btn-save" onClick={() => saveAiRoutine(r)} disabled={aiSaving}>
                      {aiSaving ? 'Salvando…' : '💾 Salvar como treino'}
                    </button>
                    <button className="wk-btn wk-btn-start" onClick={() => startAiRoutine(r)}>
                      ▶ Iniciar este treino
                    </button>
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>

      {/* ===== INICIAR OUTRO TREINO (acima do card de hoje) ===== */}
      {workoutToday && workoutToday.session && (
        <button
          className="wk-btn wk-btn-start"
          style={{ width: '100%', marginBottom: 12 }}
          onClick={() => setPickOther((v) => !v)}
        >
          {pickOther ? 'Cancelar' : '＋ Iniciar treino'}
        </button>
      )}
      {workoutToday && workoutToday.session && pickOther && (
        <section className="card workout-card" style={{ marginBottom: 12 }}>{renderPicker()}</section>
      )}

      {/* ===== TREINO DE HOJE ===== */}
      {workoutToday && (
        <section className="card workout-card">
          <div className="wstat-title-row">
            <h3>🏋️ Treino de hoje</h3>
            <InfoButton onClick={() => setShowCargaGymInfo(true)} />
          </div>
          {showCargaGymInfo && (
            <InfoModal title="Como classifico Leve / Moderado / Pesado" onClose={() => setShowCargaGymInfo(false)}>
              <p>
                Pra treino de academia, comparo a sessão de hoje com{' '}
                <b>todas as suas sessões de academia anteriores</b> (não separa por tipo de
                treino, já que aqui não tem frequência cardíaca registrada).
              </p>
              <p>
                Combino 2 fatores num z score: <b>duração total</b> da sessão e{' '}
                <b>total de séries concluídas</b> (soma de todos os exercícios). A média
                dos dois z scores decide a faixa: Pesado (acima da sua média), Moderado
                (perto da média) ou Leve (abaixo da média).
              </p>
              <p>
                Só classifico com <b>5 ou mais sessões anteriores</b> registradas. Abaixo
                disso, a sessão fica sem selo.
              </p>
              <p>Sem IA, sem modelo treinado. É estatística direta (z score) sobre o seu próprio histórico.</p>
            </InfoModal>
          )}

          {workoutToday.session && (() => {
            const exs = workoutToday.session.exercises || [];
            const exSeconds = exs.reduce((sum, ex) => sum + (ex.seconds || 0), 0);
            const pauseSeconds = exs.reduce((sum, ex) => sum + (ex.rest_seconds || 0), 0);
            const doneCount = exs.filter((ex) => ex.sets_done > 0).length;
            return (
              <div
                className="workout-today-summary"
                onClick={() => setWorkoutTodayOpen((v) => !v)}
                role="button"
                tabIndex={0}
              >
                <div className="workout-total">
                  <span>
                    ✅ Concluído às {fmtDateTime(workoutToday.session.started_at)}
                    {workoutToday.session.routine_name ? ` · ${workoutToday.session.routine_name}` : ''}
                    {workoutToday.session.carga_classificacao && (
                      <span
                        className="wstat-carga-badge"
                        style={{
                          color: workoutToday.session.carga_classificacao.color,
                          borderColor: workoutToday.session.carga_classificacao.color,
                          marginLeft: 8,
                        }}
                      >
                        {workoutToday.session.carga_classificacao.label}
                      </span>
                    )}
                  </span>
                </div>

                <div className="workout-info-rows">
                  <div className="workout-info-row"><span>Exercícios</span><b>{doneCount}/{exs.length}</b></div>
                  <div className="workout-info-row"><span>Tempo de treino</span><b>{fmtSec(exSeconds)}</b></div>
                  <div className="workout-info-row"><span>Pausas</span><b>{fmtSec(pauseSeconds)}</b></div>
                  <div className="workout-info-row"><span>Tempo total</span><b>{fmtSec(workoutToday.session.total_seconds)}</b></div>
                  {workoutToday.session.calories_kcal != null && (
                    <div className="workout-info-row"><span>Calorias</span><b>{workoutToday.session.calories_kcal} kcal</b></div>
                  )}
                </div>

                {workoutTodayOpen && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <div className="workout-exs">
                      {exs.map((ex, idx) => (
                        <div className="workout-ex" key={idx}>
                          <span className="workout-ex-name">{ex.name}</span>
                          <span className="workout-ex-meta">
                            {fmtSec(ex.seconds)}
                            {ex.sets_done ? ` · ${ex.sets_done} séries` : ''}
                            {ex.rests_done ? ` · ${ex.rests_done} pausas` : ''}
                          </span>
                        </div>
                      ))}
                    </div>

                    <button
                      className="wk-btn wk-btn-start workout-start-btn"
                      onClick={restartTodayWorkout}
                    >
                      Reiniciar treino
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

          {!workoutToday.session && renderPicker()}
        </section>
      )}

      {/* ===== TREINOS REALIZADOS (histórico, agrupado por dia) ===== */}
      {history.length > 0 && (() => {
        const dias = [];
        const porData = {};
        history.forEach((s) => {
          if (!porData[s.session_date]) {
            porData[s.session_date] = { data: s.session_date, sessoes: [] };
            dias.push(porData[s.session_date]);
          }
          porData[s.session_date].sessoes.push(s);
        });
        return (
          <section className="card workout-history-card">
            <h3>📜 Treinos realizados</h3>
            <div className="workout-history-days">
              {dias.map((dia) => (
                <div className="workout-history-day" key={dia.data}>
                  <div className="workout-history-date">
                    {fmtDateShort(dia.data)} {fmtWeekday(dia.data)}
                  </div>
                  {dia.sessoes.map((s) => (
                    <div className="workout-history-session" key={s.id}>
                      <div className="workout-history-name">{s.nome}</div>
                      <div className="workout-history-stats">
                        <div className="workout-history-stat"><b>{fmtSec(s.total_seconds)}</b><span>Tempo total</span></div>
                        {s.tipo === 'academia' ? (
                          <>
                            <div className="workout-history-stat"><b>{fmtSec(s.pause_seconds)}</b><span>Pausas</span></div>
                            <div className="workout-history-stat"><b>{s.exercises_done}/{s.exercises_total}</b><span>Exercícios</span></div>
                            {s.calorias != null && (
                              <div className="workout-history-stat"><b>{s.calorias}</b><span>Calorias</span></div>
                            )}
                          </>
                        ) : (
                          <>
                            {s.distance_km != null && (
                              <div className="workout-history-stat"><b>{s.distance_km}km</b><span>Distância</span></div>
                            )}
                            {s.calorias != null && (
                              <div className="workout-history-stat"><b>{s.calorias}</b><span>Calorias</span></div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </section>
        );
      })()}

      {/* ===== RESUMO PÓS-TREINO ===== */}
      {doneSummary && (
        <div className="wk-done">
          <span className="wk-done-title">✅ Treino salvo!</span>
          <span className="wk-done-total">Tempo total: <b>{fmtWk(doneSummary.totalSeconds)}</b></span>
          <span className="wk-done-total">Pausas: <b>{fmtWk(doneSummary.totalRestSeconds || 0)}</b></span>
          <span className="wk-done-total">Exercício líquido: <b>{fmtWk(doneSummary.netSeconds || 0)}</b></span>
          <div className="wk-done-list">
            {doneSummary.exercises.map((e, i) => (
              <div className="wk-done-row" key={i}>
                <span>{e.name}</span>
                <span>{fmtWk(e.seconds)} líquido · {fmtWk(e.rest_seconds || 0)} pausa ({e.rests_done || 0}x) · {e.sets_done} séries</span>
              </div>
            ))}
          </div>
          <div className="wk-actions">
            <button className="wk-btn wk-btn-ghost" onClick={() => setDoneSummary(null)}>Fechar</button>
            <button
              className="wk-btn wk-btn-ghost"
              onClick={() => { setDoneSummary(null); setPickOther(true); window.scrollTo?.({ top: 0, behavior: 'smooth' }); }}
            >
              ＋ Outro treino
            </button>
            <button className="wk-btn wk-btn-start" onClick={restartWorkout}>🔄 Reiniciar treino</button>
          </div>
        </div>
      )}

      {/* ===== POPUP: cadastro/edição de treino ===== */}
      {editorOpen && (
        <div className="modal-overlay" onClick={() => setEditorOpen(false)}>
          <div className="modal wk-editor-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">{selectedId ? 'Editar treino' : 'Novo treino'}</h3>

            <datalist id="wk-catalog">
              {suggestions.map((s) => <option key={s} value={s} />)}
            </datalist>

            <label className="wk-field wk-field-name">
              <span>Nome do treino</span>
              <input
                className="wk-input"
                placeholder="Ex: Pernas, Braço, Costas, Funcional…"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </label>

            <div className="wk-list">
              {editList.length === 0 && (
                <p className="wk-msg">Nenhum exercício ainda. Adicione abaixo.</p>
              )}
              {editList.map((ex, i) => (
                <div className="wk-card" key={i}>
                  <div className="wk-card-head">
                    <span className="wk-card-num">{i + 1}</span>
                    <input
                      className="wk-input wk-input-name"
                      list="wk-catalog"
                      placeholder="Exercício"
                      value={ex.name}
                      onChange={(e) => updateEx(i, 'name', e.target.value)}
                    />
                    <button className="wk-icon" onClick={() => moveEx(i, -1)} disabled={i === 0} aria-label="Subir">▲</button>
                    <button className="wk-icon" onClick={() => moveEx(i, 1)} disabled={i === editList.length - 1} aria-label="Descer">▼</button>
                    <button className="wk-icon wk-icon-del" onClick={() => removeEx(i)} aria-label="Remover">✕</button>
                  </div>
                  <div className="wk-fields">
                    <label className="wk-field">
                      <span>Carga (kg)</span>
                      <input className="wk-input" inputMode="decimal" placeholder="—" value={ex.carga}
                        onChange={(e) => updateEx(i, 'carga', e.target.value)} />
                    </label>
                    <label className="wk-field">
                      <span>Séries</span>
                      <input className="wk-input" inputMode="numeric" placeholder="—" value={ex.series}
                        onChange={(e) => updateEx(i, 'series', e.target.value)} />
                    </label>
                    <label className="wk-field">
                      <span>Reps</span>
                      <input className="wk-input" placeholder="—" value={ex.reps}
                        onChange={(e) => updateEx(i, 'reps', e.target.value)} />
                    </label>
                    <label className="wk-field">
                      <span>Descanso (s)</span>
                      <input className="wk-input" inputMode="numeric" placeholder="60" value={ex.rest}
                        onChange={(e) => updateEx(i, 'rest', e.target.value)} />
                    </label>
                  </div>
                </div>
              ))}
            </div>

            <button className="wk-btn wk-btn-add" onClick={addEx}>＋ Adicionar exercício</button>

            {error && <p className="wk-msg wk-err">{error}</p>}

            <div className="modal-actions">
              <button className="wk-btn wk-btn-ghost" onClick={() => setEditorOpen(false)}>Fechar</button>
              {selectedId && (
                <button className="wk-btn wk-btn-cancel" onClick={deleteRoutine} disabled={saving}>
                  Excluir
                </button>
              )}
              <button className="wk-btn wk-btn-save" onClick={saveRoutine} disabled={saving}>
                {saving ? 'Salvando…' : savedMsg ? '✓ Salvo' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="wk-spacer" />
    </div>
  );
}

export default WorkoutTab;
